/**
 * useRecaptchaToken.ts
 *
 * Custom hook for acquiring Google reCAPTCHA v3 tokens.
 * Centralises the executeRecaptcha null-check and error toast so each auth
 * page gets a single getToken(action) call instead of repeating the guard.
 *
 * @version 1.0.0
 * @author Frontend Team
 * @date 2026-04-07
 * @modified 2026-04-07
 * @copyright © 2026 Thesis Pte Ltd. All rights reserved.
 */

import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { useRef } from "react"
import { toast } from "react-toastify"
import { MESSENGER_NOTIFICATION } from "constants/messenger"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import * as Sentry from "@sentry/react"

const RECAPTCHA_RETRY_INTERVAL_MS = 200
const RECAPTCHA_TIMEOUT_MS = 5000

export const useRecaptchaToken = () => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const executeRecaptchaRef = useRef(executeRecaptcha)
  executeRecaptchaRef.current = executeRecaptcha

  const isReady = !!executeRecaptcha

  const getToken = async (action: string): Promise<string> => {
    const execute = await new Promise<NonNullable<typeof executeRecaptcha>>(
      (resolve, reject) => {
        const start = Date.now()
        const check = () => {
          if (executeRecaptchaRef.current) {
            return resolve(executeRecaptchaRef.current)
          }
          if (Date.now() - start >= RECAPTCHA_TIMEOUT_MS) {
            return reject(new Error(MESSENGER_NOTIFICATION.RECAPTCHA_AVAILABLE))
          }
          setTimeout(check, RECAPTCHA_RETRY_INTERVAL_MS)
        }
        check()
      }
    ).catch((err) => {
      toast(
        <LabelNotificationPage
          messenger={MESSENGER_NOTIFICATION.RECAPTCHA_AVAILABLE}
          type="error"
        />
      )
      Sentry.captureException(err)
      throw err
    })

    return execute(action)
  }

  return { getToken, isReady }
}
